/**
 * Product Image Auto-Compression Utility
 * Compresses any uploaded image or Base64 string to max 512x512 WebP/JPEG
 * drastically reducing disk space, database payload, and network transfer.
 */
export async function compressProductImage(
  fileOrDataUrl: File | Blob | string,
  maxWidth = 512,
  maxHeight = 512,
  quality = 0.85
): Promise<string> {
  return new Promise((resolve, reject) => {
    // If it's already an HTTP / CDN URL, do not re-process
    if (typeof fileOrDataUrl === 'string' && (fileOrDataUrl.startsWith('http://') || fileOrDataUrl.startsWith('https://') || fileOrDataUrl.startsWith('/'))) {
      return resolve(fileOrDataUrl);
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        let width = img.width;
        let height = img.height;

        // Calculate aspect ratio scaling to fit inside maxWidth x maxHeight
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          if (typeof fileOrDataUrl === 'string') resolve(fileOrDataUrl);
          else resolve('');
          return;
        }

        // High quality image smoothing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        // Try modern WebP format first
        try {
          const webpData = canvas.toDataURL('image/webp', quality);
          if (webpData.startsWith('data:image/webp')) {
            resolve(webpData);
            return;
          }
        } catch {
          // WebP canvas encoding fallback
        }

        // Fallback to high quality JPEG
        const jpegData = canvas.toDataURL('image/jpeg', quality);
        resolve(jpegData);
      } catch (err) {
        if (typeof fileOrDataUrl === 'string') resolve(fileOrDataUrl);
        else reject(err);
      }
    };

    img.onerror = () => {
      if (typeof fileOrDataUrl === 'string') resolve(fileOrDataUrl);
      else reject(new Error('Failed to load image for compression'));
    };

    if (typeof fileOrDataUrl === 'string') {
      img.src = fileOrDataUrl;
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target?.result as string;
      };
      reader.onerror = reject;
      reader.readAsDataURL(fileOrDataUrl);
    }
  });
}
